/**
 * Risco psicossocial vindo do app do Clube Lidera.
 *
 * O COPSOQ respondido no Clube é agregado lá e chega aqui k-anônimo: distribuição de pessoas
 * pelas três faixas, sem score e sem resposta individual. Esta tela existe para esse resultado
 * entrar no PGR pela mesma porta do painel interno.
 *
 * Duas ausências são estado normal e a tela precisa dizê-las com todas as letras, porque as
 * duas parecem "sem risco" para quem lê rápido:
 *
 * - **dimensão suprimida** — houve resposta, mas menos que o piso de k-anonimato, então o
 *   Clube não publica. Setor pequeno cai aqui o tempo todo;
 * - **dimensão não medida** — a forma aplicada não tem aquela dimensão. A curta mede 24 das 41.
 *
 * Nenhuma das duas é risco zero, e tratá-las como zero é como um ponto cego vira tranquilidade.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Groups as GroupsIcon,
  Info as InfoIcon,
  Psychology as PsychologyIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import { PageShell, DataTable } from '../../components/common';
import { isApiError } from '../../types/common';
import type { DataTableColumn } from '../../components/common';
import { CompanySelectField } from '../../components/forms/CompanySelectField';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import clubeLideraService, {
  type DimensaoDeRisco,
  type MotivoDaAusencia,
  type ResultadoDoClube,
  type SugestaoDoClube,
} from '../../services/clubeLideraService';

/** Cor e palavra de cada nível, iguais às do painel interno — é o mesmo risco, com outra origem. */
const NIVEL = {
  alto: { rotulo: 'Risco alto', cor: 'error' as const },
  atencao: { rotulo: 'Atenção', cor: 'warning' as const },
  baixo: { rotulo: 'Risco baixo', cor: 'success' as const },
};

/**
 * O que dizer quando não há resultado.
 *
 * Três motivos, três conversas diferentes: uma é com o time técnico, outra é comercial e a
 * terceira é com o RH. Um único "sem dados" mandaria as três pessoas para o lugar errado.
 */
const AUSENCIA: Record<MotivoDaAusencia, { titulo: string; texto: string }> = {
  'integracao-desligada': {
    titulo: 'A integração com o Clube Lidera não está ativa nesta instalação',
    texto:
      'Os questionários respondidos no app não chegam aqui enquanto ela estiver desligada. ' +
      'Peça ao time técnico para configurar a integração.',
  },
  'empresa-sem-clube': {
    titulo: 'Esta empresa não está vinculada ao Clube Lidera',
    texto:
      'O Clube é contratado à parte, e só as empresas que o assinam respondem o COPSOQ pelo app. ' +
      'Para esta empresa, o caminho é a avaliação psicossocial interna.',
  },
  'sem-respostas': {
    titulo: 'Ninguém desta empresa respondeu o COPSOQ no app ainda',
    texto:
      'O resultado aparece aqui assim que houver respostas suficientes — o Clube só publica um ' +
      'recorte quando ele reúne gente bastante para ninguém ser identificado.',
  },
};

export const PsychosocialClubeRiscoPage: React.FC = () => {
  const { effectiveCompanyId, shouldShowCompanyField } = useCompanyContext();

  const [companyId, setCompanyId] = useState<number | null>(effectiveCompanyId);
  const [resultado, setResultado] = useState<ResultadoDoClube | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sugestao, setSugestao] = useState<SugestaoDoClube | null>(null);
  const [sugerindo, setSugerindo] = useState(false);
  const [erroDaSugestao, setErroDaSugestao] = useState<string | null>(null);

  useEffect(() => {
    if (effectiveCompanyId) setCompanyId(effectiveCompanyId);
  }, [effectiveCompanyId]);

  const buscar = useCallback(async (empresa: number) => {
    setCarregando(true);
    setErro(null);
    try {
      setResultado(await clubeLideraService.risco(empresa));
    } catch {
      // A mensagem não repete o erro do servidor: falha de integração é sempre a mesma história
      // para quem está na tela, e o detalhe técnico já foi para o log do servidor.
      setErro('Não foi possível consultar o Clube Lidera agora. Tente novamente em instantes.');
      setResultado(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (companyId) void buscar(companyId);
  }, [companyId, buscar]);

  const risco = resultado?.disponivel ? resultado.risco : undefined;

  /**
   * O risco do Clube só entra no PGR virando item de plano de ação — o documento não tem bloco
   * psicossocial próprio. A IA propõe; o responsável técnico revisa e assina.
   */
  const sugerirPlano = useCallback(async () => {
    if (!companyId) return;
    setSugerindo(true);
    setErroDaSugestao(null);
    try {
      setSugestao(await clubeLideraService.sugerirPlanoDeAcao(companyId));
    } catch (e) {
      // O `httpClient` já traduz a recusa do servidor num `ApiRequestError`, e o texto dela está
      // em `message` — não num `response.data.error` de axios, que o interceptor consome e não
      // repassa. Ler a forma do axios aqui devolvia `undefined` sempre, e a tela trocava o motivo
      // real ("defina ANTHROPIC_API_KEY") por um "tente novamente" que convidava a repetir um
      // pedido que nunca ia funcionar.
      setErroDaSugestao(
        (isApiError(e) && e.message) || 'Não foi possível gerar as sugestões agora. Tente novamente.'
      );
      setSugestao(null);
    } finally {
      setSugerindo(false);
    }
  }, [companyId]);

  const colunas = useMemo<DataTableColumn<DimensaoDeRisco>[]>(
    () => [
      {
        id: 'rotulo',
        label: 'Dimensão',
        primary: true,
        render: (d) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {d.rotulo}
            </Typography>
            {d.direcao === 'protective' && (
              <Tooltip title="Dimensão protetora: o resultado já considera que mais é melhor.">
                <ShieldIcon fontSize="small" color="disabled" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      { id: 'dominio', label: 'Domínio', render: (d) => d.dominio },
      {
        id: 'critico',
        label: 'Em faixa crítica',
        render: (d) => (
          <Stack spacing={0.5} sx={{ minWidth: 120 }}>
            <Typography variant="body2" fontWeight={700} color={d.pctCritico >= 30 ? 'error.main' : 'text.primary'}>
              {d.pctCritico}% ({d.critico} de {d.total})
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, d.pctCritico)}
              color={d.pctCritico >= 30 ? 'error' : 'warning'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Stack>
        ),
      },
      {
        id: 'distribuicao',
        label: 'Distribuição',
        render: (d) => (
          <Stack direction="row" spacing={0.5}>
            <Chip size="small" color="success" label={`${d.pctFavoravel}% bom`} />
            <Chip size="small" color="warning" label={`${d.pctAtencao}% atenção`} />
            <Chip size="small" color="error" label={`${d.pctCritico}% crítico`} />
          </Stack>
        ),
      },
    ],
    []
  );

  const ausencia = !resultado?.disponivel && resultado?.motivo ? AUSENCIA[resultado.motivo] : null;

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Psicossocial', path: '/psychosocial' },
        { label: 'Clube Lidera' },
      ]}
      title="Risco psicossocial — Clube Lidera"
      subtitle="COPSOQ respondido no app do colaborador, agregado e sem identificação individual"
      actions={
        risco ? (
          <Button
            variant="contained"
            size="small"
            startIcon={sugerindo ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={sugerindo}
            onClick={() => void sugerirPlano()}
          >
            Sugerir plano de ação NR-1
          </Button>
        ) : undefined
      }
    >
      {shouldShowCompanyField && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <CompanySelectField
              value={companyId ? String(companyId) : ''}
              onChange={(valor) => setCompanyId(valor ? Number(valor) : null)}
            />
          </CardContent>
        </Card>
      )}

      {erro && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {erro}
        </Alert>
      )}

      {ausencia && !carregando && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {ausencia.titulo}
          </Typography>
          <Typography variant="body2">{ausencia.texto}</Typography>
        </Alert>
      )}

      {risco && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <PsychologyIcon color="action" fontSize="small" />
                    <Typography variant="caption" color="text.secondary">
                      Risco geral da empresa
                    </Typography>
                  </Stack>
                  {/*
                    Recorte inteiramente suprimido não tem nível. Antes saía "Risco baixo —
                    reavaliar a cada 24 meses", que afirmava justamente o que a supressão
                    impede de saber. Aqui a ausência é dita com todas as letras.
                  */}
                  {risco.riscoGeral.determinado ? (
                    <>
                      <Chip
                        label={NIVEL[risco.riscoGeral.nivel].rotulo}
                        color={NIVEL[risco.riscoGeral.nivel].cor}
                        sx={{ fontWeight: 700 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        Reavaliar a cada {risco.riscoGeral.periodicidadeMeses} meses
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Chip label="Não é possível dizer" variant="outlined" sx={{ fontWeight: 700 }} />
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        Nenhuma dimensão reuniu gente bastante para ser publicada neste recorte.
                        Sem risco medido não é o mesmo que risco baixo.
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <GroupsIcon color="action" fontSize="small" />
                    <Typography variant="caption" color="text.secondary">
                      Participação
                    </Typography>
                  </Stack>
                  {risco.respondentes.responderam === null ? (
                    <>
                      {/* Suprimido: o Clube não diz quantos responderam num recorte pequeno,
                          porque o próprio número já é informação sobre o grupo. Mostrar 0 aqui
                          afirmaria "ninguém respondeu", que é outro fato. */}
                      <Typography variant="h6" fontWeight={700}>
                        — de {risco.respondentes.elegiveis}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        A adesão não é divulgada em recorte pequeno demais
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6" fontWeight={700}>
                        {risco.respondentes.responderam} de {risco.respondentes.elegiveis}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round((risco.respondentes.taxa ?? 0) * 100)}% dos convidados
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <WarningIcon color="action" fontSize="small" />
                    <Typography variant="caption" color="text.secondary">
                      Domínios em risco alto
                    </Typography>
                  </Stack>
                  <Typography variant="h6" fontWeight={700}>
                    {risco.porDominio.filter((d) => d.nivel === 'alto').length} de{' '}
                    {risco.porDominio.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    A partir de 30% em faixa crítica (NR-1)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <InfoIcon color="action" fontSize="small" />
                    <Typography variant="caption" color="text.secondary">
                      Instrumento
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={700}>
                    {risco.instrumento}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {risco.porDimensao.length} dimensões com resultado
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {risco.suprimidas > 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {risco.suprimidas}{' '}
                {risco.suprimidas === 1 ? 'dimensão ficou oculta' : 'dimensões ficaram ocultas'} por
                proteção de anonimato
              </Typography>
              <Typography variant="body2">
                Houve resposta, mas menos de {risco.kMin} pessoas no recorte — publicar o resultado
                permitiria identificar quem respondeu. <strong>Isso não significa ausência de
                risco</strong>: significa que ainda não dá para medir esse pedaço com segurança.
              </Typography>
            </Alert>
          )}

          {risco.naoReconhecidas.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              O Clube enviou {risco.naoReconhecidas.length} dimensão(ões) que este sistema ainda não
              conhece. O que está abaixo segue válido, mas avise o time técnico para atualizar a
              tabela de dimensões.
            </Alert>
          )}

          {risco.porDominio.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Risco por domínio
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {risco.porDominio.map((d) => (
                    <Chip
                      key={d.dominio}
                      label={`${d.dominio} — ${d.pctCritico}%`}
                      color={NIVEL[d.nivel].cor}
                      variant={d.nivel === 'baixo' ? 'outlined' : 'filled'}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {erroDaSugestao && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {erroDaSugestao}
        </Alert>
      )}

      {sugestao && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Plano de ação sugerido ({sugestao.acoes.length} ações)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sugestões geradas a partir do risco agregado, sem nenhum dado individual. É assim que
              o resultado do app entra no PGR — o documento não tem bloco psicossocial próprio, e o
              risco chega nele como item de plano de ação.{' '}
              <strong>Revise antes de levar ao plano: a IA propõe, o responsável técnico assina.</strong>
            </Typography>

            <Stack spacing={1.5}>
              {sugestao.acoes.map((acao, indice) => (
                <Box
                  key={`${acao.dimensao}-${indice}`}
                  sx={{ p: 1.5, borderLeft: 3, borderColor: 'primary.main', bgcolor: 'action.hover' }}
                >
                  <Stack direction="row" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={acao.priority} color="warning" />
                    <Chip size="small" label={acao.actionType} variant="outlined" />
                    <Chip size="small" label={acao.responsible} variant="outlined" />
                    <Chip size="small" label={acao.norma} variant="outlined" />
                    {acao.dimensao && <Chip size="small" label={acao.dimensao} color="primary" />}
                  </Stack>
                  <Typography variant="body2">{acao.description}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <DataTable
        columns={colunas}
        rows={risco?.porDimensao ?? []}
        getRowId={(d) => d.chave}
        loading={carregando}
        emptyState={{
          title: 'Nenhuma dimensão com resultado para esta empresa',
          description:
            'O COPSOQ do Clube é respondido no app do colaborador. Assim que houver respostas suficientes para preservar o anonimato, as dimensões aparecem aqui.',
        }}
      />

      {risco && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Dimensão que a forma aplicada não mede simplesmente não aparece nesta lista — a forma
            curta do COPSOQ mede 24 das 41 dimensões. Ausência aqui é falta de medição, não
            ausência de risco.
          </Typography>
        </Box>
      )}
    </PageShell>
  );
};

export default PsychosocialClubeRiscoPage;
